<template>
    <div>
        <h1 class="page-header">{{ $t('str.timeline.timeEntry.title') }}</h1>
        <hr />
        <div class="row">
            <div v-if="isSuperAdminMaster" class="col-md-4 mb-3">
                <label class="form-label" for="accountField">{{ $t('str.register.guard.groups.account.field') }}</label>
                <select v-model="filters.account" @change="changeAccount" class="form-select" id="accountField">
                    <option value="">{{ $t('str.register.select.placeholder') }}</option>
                    <option v-for="account in accounts" :value="account._id" :key="account._id">{{ account.name }}</option>
                </select>
            </div>

            <div class="col-md-4 mb-3">
                <label class="form-label" for="clientField">{{ $t('str.register.guard.groups.client.field') }}</label>
                <select v-model="filters.client" @change="changeClient" class="form-select" id="clientField">
                    <option value="">{{ $t('str.register.select.placeholder') }}</option>
                    <option v-for="client in clients" :value="client._id" :key="client._id">{{ client.name }}</option>
                </select>
            </div>

            <div class="col-md-4 mb-3">
                <label class="form-label" for="siteField">{{ $t('str.register.guard.groups.site.field') }}</label>
                <select v-model="filters.site" @change="changeSite" class="form-select" id="siteField">
                    <option value="">{{ $t('str.register.select.placeholder') }}</option>
                    <option v-for="site in sites" :value="site._id" :key="site._id">{{ site.name }}</option>
                </select>
            </div>

            <div class="col-md-4 mb-3">
                <label class="form-label" for="eventTypeField">{{ $t('str.timeline.timeEntry.column.eventType') }}</label>
                <select v-model="filters.eventType" @change="filterFromFirstPage" class="form-select" id="eventTypeField">
                    <option value="">{{ $t('str.timeline.alertOccurrences.status.option.all') }}</option>
                    <option value="CLOCK_IN">{{ $t('CLOCK_IN') }}</option>
                    <option value="CLOCK_OUT">{{ $t('CLOCK_OUT') }}</option>
                    <option value="BREAK_START">{{ $t('BREAK_START') }}</option>
                    <option value="BREAK_END">{{ $t('BREAK_END') }}</option>
                </select>
            </div>

            <div class="col-md-4 mb-3">
                <label class="form-label" for="statusField">{{ $t('str.range.date.field') }}</label>
                <date-range-picker
                    ref="picker"
                    class="form-select btn btn-dark me-2"
                    :opens="dateRange.opens"
                    :locale-data="dateRange.sampleLocaleData"
                    :singleDatePicker="dateRange.singleDatePicker"
                    :timePicker="dateRange.timePicker"
                    :timePicker24Hour="dateRange.timePicker24Hour"
                    :showWeekNumbers="dateRange.showWeekNumbers"
                    :showDropdowns="dateRange.showDropdowns"
                    :autoApply="dateRange.autoApply"
                    v-model="dateRange.range"
                    :max-date="dateRange.maxDate"
                    :ranges="dateRanges"
                    @update="updateValues"
                    :linkedCalendars="dateRange.linkedCalendars"
                    :date-range="dateRange"
                    :locale="dateRange.locale"
                >
                     <template v-slot:input="filters">
                        <i class="fa fa-calendar fa-fw text-white text-opacity-50 ms-n1"></i>
                        {{ formatDate(filters.startDate).split(' - ')[0] }} - {{ formatDate(filters.endDate).split(' - ')[0] }}
                        <b class="caret ms-1 opacity-5"></b>
                    </template>
                </date-range-picker>
            </div>

            <div class="col-md-2 text-center align-self-end mb-3">
                <button @click="filterFromFirstPage" type="submit" class="w-100 btn btn-primary is-loading">
                    <i v-if="isLoading" class="fas fa-spinner fa-pulse"></i>
                    {{ $t('str.btn.search') }}
                </button>
            </div>
        </div>

        <div>
            <vue-good-table
                :columns="columns"
                :rows="items"
                :totalRows="totalItems"
                :search-options="{ enabled: true, placeholder: $t('str.table.search.in.this.table') }"
                :pagination-options="paginationOptions"
                @on-page-change="onPageChange"
                @on-per-page-change="onPerPageChange"
                @on-row-click="selectItem"
            >
                <div slot="emptystate" class="vgt-center-align vgt-text-disabled">
                    <i v-if="isLoading" class="fas fa-spinner fa-spin" />
                    <span v-if="!isLoading && items?.length === 0">{{ $t('str.table.subtitle.no.data') }}</span>
                </div>

                <template slot="table-row" slot-scope="props">
                    <span v-if="props.column.field === 'account' || props.column.field === 'client' || props.column.field === 'site'"> {{ props.formattedRow[props.column.field]?.name }} </span>
                    <span v-else-if="props.column.field === 'user'">{{ props.formattedRow[props.column.field]?.fullName || props.formattedRow[props.column.field] }}</span>
                    <span v-else-if="props.column.field === 'timestamp'">{{ formatDate(props.formattedRow[props.column.field]) }}</span>
                    <span v-else-if="props.column.field === 'eventType'">
                        <span
                            class="badge"
                            v-bind:class="{
                                'bg-green': props.formattedRow[props.column.field] === 'CLOCK_IN',
                                'bg-red': props.formattedRow[props.column.field] === 'CLOCK_OUT',
                                'bg-gray': props.formattedRow[props.column.field] === 'BREAK_START' || props.formattedRow[props.column.field] === 'BREAK_END',
                            }"
                        >
                            {{ $t(props.formattedRow[props.column.field]) }}    
                        </span>
                    </span>
                    <span v-else>{{ props.formattedRow[props.column.field] }}</span>
                </template>
            </vue-good-table>
        </div>
        <TimeEntryDetailModal :selectedItem="selectedItem" />
        <notifications group="top-right" position="top right" :speed="1000" />
    </div>
</template>

<script>
import moment from 'moment-timezone'
import Controller from './CrtTimeEntry.vue'
import Common from '../../../common/Common.vue'
import TimeEntryDetailModal from './TimeEntryDetailModal.vue'
import { createTimelineDateRange } from '../../../utils/timeline'

export default {
    components: { TimeEntryDetailModal },
    data() {
        const accountTimezone = Common.getAccountTimezone()
        return {
            accounts: [],
            clients: [],
            sites: [],
            items: [],
            columns: [],
            isLoading: false,
            dateRange: createTimelineDateRange(accountTimezone),
            userLocale: 'pt-br',
            maxDate: moment.tz(accountTimezone).endOf('day').toDate(),
            dateRanges: {
                [this.$t('str.date.today')]: [moment.tz(accountTimezone).toDate(), moment.tz(accountTimezone).toDate()],
                [this.$t('str.date.yesterday')]: [moment.tz(accountTimezone).subtract(1, 'days').toDate(), moment.tz(accountTimezone).subtract(1, 'days').toDate()],
                [this.$t('str.date.this.month')]: [moment.tz(accountTimezone).startOf('month').toDate(), moment.tz(accountTimezone).toDate()],
                [this.$t('str.date.last.month')]: [moment.tz(accountTimezone).subtract(1, 'month').startOf('month').toDate(), moment.tz(accountTimezone).subtract(1, 'month').endOf('month').toDate()],
            },

            totalItems: 0,
            currentPage: 1,
            paginationOptions: { perPage: 10, enabled: true, mode: 'pages' },
            filters: {
                account: '',
                client: '',
                site: '',
                eventType: '',
                startDate: moment.tz(accountTimezone),
                endDate: moment.tz(accountTimezone),
                limit: 10,
                skip: 1,
                isDescSortByStartDate: false,
                isSortByStartDate: true,
            },
            isSuperAdminMaster: false,
            role: '',
            selectedItem: null,
        }
    },
    created() {
        Controller.init(this)
        const state = this
        state.$registerEvent = state.$registerEvent || {}
    },
    methods: Controller.methods,
}
</script>

<style lang="scss" scoped>
.vue-daterange-picker.btn {
    padding: 2px !important;
    display: flex !important;
    background-color: var(--app-component-bg) !important;
    text-align-last: start !important;
}
.caret {
    display: none !important;
}
</style>
